import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Col, Row, Table } from 'antd'
import { Formik } from 'formik'

import { BenefitParticipationStatus, CompanyUserStatus, PagedList, ProductIds } from 'core'
import { InspectletKey } from 'core/application/commons/config'
import store from 'core/application/commons/localStorage'
import getCompanyEmployees, {
  CompanyEmployee,
  CompanyEmployeeFilter,
} from 'core/application/csiDashboard/csiSearch/getCompanyEmployees'
import impersonate, { ImpersonateResponse } from 'core/application/csiDashboard/impersonate/impersonate'

import { Button, DefaultSelect, Modal } from '../../../../../common/components'
import FormikDefaultInputSearchField from '../../../../../common/components/dataEntry/formik/FormikDefaultInputSearchField'
import { formatDateTimeAMPM, parseZoneFormat } from '../../../../../common/dateFormatters'
import { mapEnumObjectValues } from '../../../../../common/enumFormatters'
import { useCommandRequest, useQueryRequest } from '../../../../../common/hooks'
import SetPasswordForUserModal from './SetPasswordForUserModal'

type PeopleProps = {
  companyName: string
  companyId: string
  productId: number
}

const People: React.FC<PeopleProps> = ({ companyId, companyName, productId }) => {
  const { t: translate } = useTranslation()

  const [filter, setFilter] = useState<CompanyEmployeeFilter>({
    pageNumber: 1,
    pageSize: 10,
    orderColumn: 'createdAt',
    orderDescending: false,
    companyId,
    productId,
  })

  const [people, isPeopleLoading, sendGetPeople] = useQueryRequest<PagedList<CompanyEmployee>>(null)
  const [sendImpersonateCommand, isImpersonateCommandLoading] = useCommandRequest<ImpersonateResponse>()
  const [userToSetPassword, setUserToSetPassword] = useState<CompanyEmployee | null>(null)

  useEffect(() => {
    ;(async () => {
      await sendGetPeople(() => getCompanyEmployees(filter))
    })()
  }, [filter])

  const getRoles = (roles: string[]) => {
    const result = roles.map((s) => (s = s.replace(/([A-Z])/g, ' $1').trim()))
    return result.join('; ')
  }

  const isIchra = productId === ProductIds.IchraId

  const getColumns = (): any[] => {
    const columns = [
      {
        title: translate('csi-dashboard.people.full-name/userid'),
        dataIndex: 'fullName',
        key: 'fullName',
        sorter: false,
        sortDirections: ['descend', 'ascend'],
        render: (text: string, record: any) => {
          return (
            <span>
              <div>
                <p>{record.fullName}</p>
                <p>{record.id}</p>
              </div>
            </span>
          )
        },
      },
      {
        title: translate('general.email'),
        dataIndex: 'workEmail',
        key: 'workEmail',
        sorter: true,
        sortDirections: ['descend', 'ascend'],
        render: (text: string, record: any) => (
          <span>
            <div className="reimburse_row">
              <p>{record.workEmail}</p>
            </div>
          </span>
        ),
      },

      {
        title: translate('csi-dashboard.people.roles'),
        dataIndex: 'eligibilityStatus',
        key: 'eligibilityStatus',
        sorter: false,

        render: (text: string, record: any) => (
          <span>
            <p>{getRoles(record.roles)}</p>
          </span>
        ),
      },
      {
        title: translate('csi-dashboard.people.eligibility-status'),
        dataIndex: 'roles',
        key: 'roles',
        sorter: false,

        render: (text: string, record: any) => (
          <span>
            <p>
              {record.eligible
                ? translate('csi-dashboard.people.eligible')
                : translate('csi-dashboard.people.ineligible')}
            </p>
          </span>
        ),
      },
      {
        title: translate('csi-dashboard.people.pk-status'),
        dataIndex: 'status',
        key: 'status',
        sorter: false,

        render: (text: string, record: any) => (
          <span>
            <div className="reimburse_row">
              <p>{record.status}</p>
            </div>
          </span>
        ),
      },
      {
        title: translate('csi-dashboard.people.benefit-participation-status'),
        dataIndex: 'status',
        key: 'benefitParticipationStatus',
        sorter: false,

        render: (text: string, record: any) => (
          <span>
            <div className="reimburse_row">
              <p>{mapEnumObjectValues(BenefitParticipationStatus)[record.benefitParticipationStatus]}</p>
            </div>
          </span>
        ),
      },
      {
        title: translate('general.created-at'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        sorter: true,
        sortDirections: ['descend', 'ascend'],

        render: (text: string, record: any) => (
          <span>
            <p>{formatDateTimeAMPM(record.createdAt)}</p>
          </span>
        ),
      },
      {
        title: translate('csi-dashboard.people.termination-date'),
        dataIndex: 'terminationDate',
        key: 'terminationDate',
        sorter: true,
        sortDirections: ['descend', 'ascend'],
        render: (terminationDate: Date) => (terminationDate ? parseZoneFormat(terminationDate) : null),
      },
      {
        title: 'Inspectlet',
        sorter: false,
        render: (record: CompanyEmployee) => (
          <span>
            <div className="reimburse_row">
              <a
                href={`https://www.inspectlet.com/dashboard/captures/${InspectletKey}?displayname=${record.workEmail.replace(
                  '@',
                  '%40',
                )}`}
                target="_blank"
              >
                <Button type="default">{translate('csi-dashboard.people.inspectlet')}</Button>
              </a>
            </div>
          </span>
        ),
      },
      {
        title: 'Impersonate',
        dataIndex: '',
        key: '',
        sorter: false,
        render: (text: string, record: any) => (
          <span>
            <div className="reimburse_row">
              <Button disabled={!record.canBeImpersonate} type="default" onClick={() => onImpersonateUser(record.id)}>
                {translate('csi-dashboard.people.impersonate')}
              </Button>
            </div>
          </span>
        ),
      },
      {
        title: 'Actions',
        dataIndex: '',
        key: '',
        sorter: false,
        render: (_: string, record: CompanyEmployee) => (
          <span>
            <div className="reimburse_row">
              <Button
                type="default"
                disabled={!record.canSetPassword}
                onClick={() => {
                  setUserToSetPassword(record)
                }}
              >
                Set Password
              </Button>
            </div>
          </span>
        ),
      },
    ]

    const columnToSort: any = columns.find((c) => c.key === filter.orderColumn)
    columnToSort.defaultSortOrder = filter.orderDescending ? 'descend' : 'ascend'

    return !isIchra ? columns.filter((x) => x.key !== 'benefitParticipationStatus') : columns
  }

  const roles = [
    {
      id: undefined,
      name: 'All Roles',
    },
    {
      id: 5,
      name: 'Admin',
    },
    {
      id: 4,
      name: 'Product User',
    },
  ]

  const statuses = [
    {
      id: undefined,
      name: 'All except terminated',
    },
    {
      id: CompanyUserStatus.New,
      name: 'New',
    },
    {
      id: CompanyUserStatus.Active,
      name: 'Active',
    },
    {
      id: CompanyUserStatus.Terminated,
      name: 'Terminated',
    },
    {
      id: CompanyUserStatus.Invited,
      name: 'Invited',
    },
  ]

  const onImpersonateUser = async (userId: string) => {
    const result = await sendImpersonateCommand(() => impersonate(userId))

    if (result.completed) {
      window.open(`/?token=${result.data.token}`, '_blank')
    }
  }

  const onCancel = async () => {
    setFilter({
      ...filter,
      q: null,
      userRole: undefined,
      status: undefined,
    })
  }

  const onTableChange = (pagination: any, filters: any, sorter: any) => {
    setFilter({
      ...filter,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
      orderColumn: sorter.field || 'createdAt',
      orderDescending: sorter.order !== 'ascend',
    })
  }

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={filter}
        onSubmit={(values) => {
          setFilter({
            ...filter,
            ...values,
          })
        }}
      >
        {(formik) => (
          <form onSubmit={formik.handleSubmit} autoComplete="off">
            <Row gutter={16}>
              <Col span={4}>
                <FormikDefaultInputSearchField name={'q'} placeholder={'Search by name or email...'} />
              </Col>
              <Col span={4}>
                <DefaultSelect name="userRole" placeholder={'Select role'} items={roles}></DefaultSelect>
              </Col>
              <Col span={4}>
                <DefaultSelect name="status" placeholder={'Select status'} items={statuses}></DefaultSelect>
              </Col>
              <Col span={4}>
                <Button
                  htmlType={'submit'}
                  className={'mr-16'}
                  loading={isPeopleLoading}
                  disabled={isPeopleLoading}
                  type="primary"
                >
                  {' '}
                  {translate('general.search')}
                </Button>

                <Button onClick={() => onCancel()} type="default">
                  {translate('general.clear')}
                </Button>
              </Col>
            </Row>
          </form>
        )}
      </Formik>

      <Row>
        <Col className="mt-16" span={24}>
          <Table
            dataSource={people?.items}
            onChange={onTableChange}
            loading={isPeopleLoading}
            columns={getColumns()}
            pagination={{
              total: people?.totalItemCount,
              pageSize: people?.pageSize,
              current: people?.pageNumber,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '30', '40'],
            }}
            scroll={{ x: 'auto' }}
          />
        </Col>
      </Row>

      <SetPasswordForUserModal
        user={userToSetPassword}
        onCancel={() => setUserToSetPassword(null)}
        onPasswordChanged={() => setUserToSetPassword(null)}
      />
    </>
  )
}

export default People
